import { useEffect, useRef, useState } from "react";

// TEMP DEBUG START
// This will log once at module load
// If this shows undefined, Vite did not load the variable.
console.log('[DEBUG env] import.meta.env.VITE_CARD_PRICES_URL =', (import.meta as any).env?.VITE_CARD_PRICES_URL);
// TEMP DEBUG END

function getEnv(key: string): string | undefined {
  const viteVal = (import.meta as any)?.env?.[key];
  if (typeof viteVal === "string" && viteVal.trim().length) return viteVal.trim();
  if (typeof process !== "undefined" && (process as any).env) {
    const v = (process as any).env[key];
    if (typeof v === "string" && v.trim().length) return v.trim();
  }
  return undefined;
}

export type VendorTriple = { low: number | null; mid: number | null; market: number | null };
export type Vendors = Record<string, VendorTriple | undefined>;
type ApiResponse = {
  ok: boolean;
  sid: string;
  vendors: Vendors;
  average: { low: number | null; mid: number | null; market: number | null };
  updatedAt: string | null;
};

const MEMO: Record<string, { t: number; data: ApiResponse }> = {};
const TTL = 60_000;

export function useVendorPrices(scryfallId?: string | null) {
  // Read & trim (handles accidental trailing spaces)
  const rawBase =
    getEnv("VITE_CARD_PRICES_URL") ||
    getEnv("NEXT_PUBLIC_CARD_PRICES_URL") ||
    "";
  const base = rawBase.trim();

  const sid = (scryfallId || "").trim();
  const [data, setData] = useState<ApiResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!base || !sid) return;
    const key = `${base}?sid=${encodeURIComponent(sid)}`;
    const now = Date.now();
    const cached = MEMO[key];
    if (cached && now - cached.t < TTL) {
      setData(cached.data);
      return;
    }
    setLoading(true);
    setError(null);
    const ac = new AbortController();
    abortRef.current = ac;
    fetch(key, { signal: ac.signal, cache: "no-store" })
      .then(async r => {
        if (r.status === 404) {
          // Graceful: no pricing yet for this card
          return {
            ok: false,
            sid,
            vendors: {},
            average: { low: null, mid: null, market: null },
            updatedAt: null,
          } as ApiResponse;
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<ApiResponse>;
      })
      .then((j: ApiResponse) => {
        MEMO[key] = { t: Date.now(), data: j };
        setData(j);
      })
      .catch(e => {
        if ((e as any)?.name !== "AbortError") setError(e as Error);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [base, sid]);

  const tcg = data?.vendors?.tcgplayer;
  const cardkingdom = data?.vendors?.cardkingdom;
  const avg = data?.average;

  return {
    data,
    error,
    isLoading: !!base && !!sid && loading,
    isValidating: loading,
    tcg,
    cardkingdom,
    averageMarket: avg?.market ?? null,
    updatedAt: data?.updatedAt ?? null,
    endpointConfigured: base.length > 0,
  };
}
